import {
    call,
    delay,
    getContext,
    put,
    select,
    takeLatest,
    race,
    take,
    cancelled,
} from 'redux-saga/effects';
import {
    selectSelectedOperation,
    selectSelectedOperationId,
} from '../../redux/selectors/operationSelectors';
import {
    downloadMultipleBillingStatus,
    setBillableEntities,
    setBillingEntities,
    setBillingEntityLogo,
    setBillingPlans,
    setBillingSubscriptions,
    setError,
    setInvoiceGenerationProgress,
    setBillRun,
    setInvoices,
    setInvoicesLoading,
    setLastInvoiceDate,
    setNotif,
    unsetInvoiceNotif,
} from '../../redux/actions';
import {
    CANCEL_GET_GENERATION_PROGRESS,
    CANCEL_INVOICE,
    CHANGE_PAYMENT_STATUS,
    CREATE_BILLABLE_ENTITY,
    CREATE_BILLING_ENTITY,
    CREATE_BILLING_PLAN,
    CREATE_BILLING_SUBSCRIPTION,
    CREATE_MULTIPLE_SUBSCRIPTIONS,
    DELETE_BILLING_SUBSCRIPTION,
    DELETE_INVOICES,
    DOWNLOAD_ACCOUNTING_SUMMARY,
    DOWNLOAD_INVOICE,
    DOWNLOAD_ZIP_INVOICES,
    GENERATE_INVOICES_ASYNC,
    GENERATE_INVOICES_SYNC,
    GET_BILLABLE_ENTITIES,
    GET_BILLING_ENTITIES,
    GET_BILLING_ENTITY_LOGO,
    GET_BILLING_PLANS,
    GET_BILLING_SUBSCRIPTIONS,
    GET_INVOICES,
    GET_LAST_INVOICE_DATE,
    GET_LAST_BILL_RUN,
    PUBLISH_INVOICES,
    UPDATE_BILLABLE_ENTITY,
    UPDATE_BILLING_ENTITY,
    UPDATE_BILLING_PLAN,
} from '../../redux/reducers/constants';
import i18n from 'i18next';
import { errorHandler } from '../common/errorHandler';
import {
    selectActiveProducerId,
    selectConsumers,
    selectProducers,
} from '../../redux/selectors/participantSelectors';
import { selectUser } from '../../redux/selectors/authenticationSelectors';
import {
    makeSelectInvoicesByMonthAndBillingEntity,
    selectBillableEntities,
    selectBillingEntities,
} from '../../redux/selectors/billingNewSelector';

function* getBillingEntities() {
    try {
        const operation = yield select(selectSelectedOperation);
        const producerId = yield select(selectActiveProducerId);
        const billingGateway = yield getContext('billingNewGateway');
        const user = yield select(selectUser);

        let billingEntities;

        if (user.role === 'participant') {
            billingEntities = yield call(
                billingGateway.getProducerBillingEntities,
                producerId
            );
        } else {
            billingEntities = yield call(
                billingGateway.getBillingEntities,
                operation.id
            );
        }

        yield put(setBillingEntities(billingEntities));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* getBillableEntities() {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        let billableEntities = yield call(
            billingGateway.getBillableEntities,
            operation.id
        );
        yield put(setBillableEntities(billableEntities));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* createBillingEntity(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        yield call(
            billingGateway.createBillingEntity,
            operation.id,
            action.entity
        );
        let billingEntities = yield call(
            billingGateway.getBillingEntities,
            operation.id
        );
        yield put(setBillingEntities(billingEntities));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* updateBillingEntity(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        const { entity, formData } = action;
        let billingLogoPath;
        if (formData) {
            billingLogoPath = yield call(
                billingGateway.uploadBillingEntityLogo,
                entity,
                operation,
                formData
            );
            if (entity && billingLogoPath) {
                entity.billingLogo = billingLogoPath;
            }
        }
        yield call(billingGateway.updateBillingEntity, operation.id, entity);
        let billingEntities = yield call(
            billingGateway.getBillingEntities,
            operation.id
        );
        yield put(setBillingEntities(billingEntities));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* createBillableEntity(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        yield call(
            billingGateway.createBillableEntity,
            operation.id,
            action.entity
        );
        let billableEntities = yield call(
            billingGateway.getBillableEntities,
            operation.id
        );
        yield put(setBillableEntities(billableEntities));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* updateBillableEntity(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        yield call(
            billingGateway.updateBillableEntity,
            operation.id,
            action.entity
        );
        let billableEntities = yield call(
            billingGateway.getBillableEntities,
            operation.id
        );
        yield put(setBillableEntities(billableEntities));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* getInvoices() {
    try {
        yield put(setInvoicesLoading(true));
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        let invoices = yield call(billingGateway.getInvoices, operation.id);
        yield put(setInvoices(invoices));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* getLastInvoiceDate(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        let lastInvoiceDate = yield call(
            billingGateway.getLastInvoiceDate,
            operation.id,
            action.billingEntityId,
            action.billableEntityId
        );
        yield put(setLastInvoiceDate(lastInvoiceDate));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* generateInvoicesSync(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const { operationId, date } = action;
        yield put(
            setNotif({
                message: i18n.t(
                    'enoapp.administrative.billing.invoices.generateInProgress'
                ),
            })
        );
        const generateResults = yield call(
            billingGateway.generateInvoices,
            operationId,
            date,
            'SYNC'
        );
        let invoices = yield call(billingGateway.getInvoices, operationId);
        yield put(setInvoices(invoices));
        const nonZeroInvoices = generateResults.invoices.filter(
            (invoice) => invoice.status !== 'NOT_CHARGED'
        );
        if (generateResults.errors.length === 0) {
            yield put(
                setNotif({
                    message: i18n.t(
                        'enoapp.administrative.billing.invoices.generateSuccess',
                        {
                            count: nonZeroInvoices.length,
                        }
                    ),
                })
            );
        } else if (nonZeroInvoices.length > 0) {
            yield put(
                setNotif({
                    message: i18n.t(
                        'enoapp.administrative.billing.invoices.generateSuccess',
                        {
                            count: nonZeroInvoices.length,
                        }
                    ),
                    variant: 'warning',
                    persist: true,
                })
            );
        }

        if (generateResults.errors.length > 0) {
            const billingEntities = yield select(selectBillingEntities);
            const billableEntities = yield select(selectBillableEntities);
            let errorMessages = [];
            for (let error of generateResults.errors) {
                // I18N error if possible
                const i18nErrorMessage = error.failure.includes(' ')
                    ? error.failure
                    : i18n.t('error.invoices.' + error.failure);

                errorMessages.push(
                    i18n.t(
                        'enoapp.administrative.billing.invoices.generateError',
                        {
                            billingEntity: billingEntities.find(
                                (billingEntity) =>
                                    billingEntity.id === error.billingEntityId
                            )?.name,
                            billableEntity: billableEntities.find(
                                (billableEntity) =>
                                    billableEntity.id === error.billableEntityId
                            )?.name,
                        }
                    ) + i18nErrorMessage
                );
            }
            yield put(
                setError({
                    message: errorMessages.join('\n'),
                    persist: true,
                })
            );
        }
    } catch (error) {
        yield errorHandler(error);
    }
}

function* generateInvoicesAsync(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const { operationId, date } = action;
        yield put(setBillRun(null));
        yield put(setInvoiceGenerationProgress({}));
        const generateResult = yield call(
            billingGateway.generateInvoices,
            operationId,
            date,
            'ASYNC'
        );
        if (generateResult.id) {
            yield call(getGenerationProgress, {
                billRun: generateResult,
            });
        } else {
            yield put(setInvoiceGenerationProgress(null));
            yield call(getLastBillRun);
            yield put(
                setNotif({
                    message: i18n.t(
                        'enoapp.administrative.billing.billRun.noInvoiceGenerated'
                    ),
                    variant: 'warning',
                })
            );
        }
    } catch (error) {
        yield errorHandler(error);
    }
}

function* watchGenerationProgress(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const { billRun } = action;
        const activeProducerId = yield select(selectActiveProducerId);
        const operationId = yield select(selectSelectedOperationId);
        const jobId = billRun.id;
        yield put(
            setInvoiceGenerationProgress({
                total: '-',
                success: 0,
                failure: 0,
            })
        );
        let jobFinished = false;
        while (!jobFinished) {
            try {
                let jobProgress;
                if (activeProducerId) {
                    jobProgress = yield call(
                        billingGateway.getParticipantGenerationProgress,
                        activeProducerId,
                        jobId
                    );
                } else {
                    jobProgress = yield call(
                        billingGateway.getGenerationProgress,
                        operationId,
                        jobId
                    );
                }
                if (jobProgress.pending === 0) {
                    yield put(setInvoiceGenerationProgress(null));
                    jobFinished = true;
                } else {
                    yield put(setInvoiceGenerationProgress(jobProgress));
                }
                yield delay(1000);
            } catch (err) {
                yield put(
                    setError({
                        message: err.message,
                        persist: true,
                    })
                );
                jobFinished = true;
            }
        }
        yield call(getLastBillRun);
    } finally {
        if (yield cancelled()) {
            yield put(setInvoiceGenerationProgress(null));
        }
    }
}

function* getGenerationProgress(action) {
    yield race({
        task: call(watchGenerationProgress, action),
        cancel: take(CANCEL_GET_GENERATION_PROGRESS),
    });
}

function* deleteInvoices(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const { operationId, invoiceIds } = action;
        const activeProducerId = yield select(selectActiveProducerId);
        if (activeProducerId) {
            yield call(
                billingGateway.deleteProducerInvoices,
                activeProducerId,
                invoiceIds
            );
        } else {
            yield call(billingGateway.deleteInvoices, operationId, invoiceIds);
        }
        let invoices = yield call(billingGateway.getInvoices, operationId);
        yield put(setInvoices(invoices));
        yield put(unsetInvoiceNotif(invoiceIds));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* downloadAccountingSummary(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const { billingEntityId, periodEndMonth } = action;
        const invoices = yield select(
            makeSelectInvoicesByMonthAndBillingEntity(
                periodEndMonth,
                billingEntityId,
                false
            )
        );
        const legacyInvoices = invoices.filter(
            (invoice) => invoice.isLegacyBill
        );
        const newInvoices = invoices.filter((invoice) => !invoice.isLegacyBill);
        if (newInvoices.length) {
            const user = yield select(selectUser);
            let url;
            if (user.role === 'participant') {
                const activeProducerId = yield select(selectActiveProducerId);
                url = yield call(
                    billingGateway.getProducerAccountingSummaryDownloadURL,
                    activeProducerId,
                    newInvoices.map((invoice) => invoice.id)
                );
            } else {
                const operationId = yield select(selectSelectedOperationId);
                url = yield call(
                    billingGateway.getAccountingSummaryDownloadURL,
                    operationId,
                    newInvoices.map((invoice) => invoice.id)
                );
            }
            window.open(url);
        }
        if (legacyInvoices.length) {
            yield put(
                downloadMultipleBillingStatus(
                    billingEntityId,
                    legacyInvoices,
                    periodEndMonth
                )
            );
        }
    } catch (error) {
        yield errorHandler(error);
    }
}
function* downloadZipInvoices(action) {
    try {
        const { billingEntityId, periodEndMonth, withNotCharged } = action;
        const invoices = yield select(
            makeSelectInvoicesByMonthAndBillingEntity(
                periodEndMonth,
                billingEntityId,
                withNotCharged
            )
        );
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        const { role } = yield select(selectUser);
        const activeProducerId = yield select(selectActiveProducerId);
        let res;
        const invoicesIds = invoices
            .filter((invoice) => !invoice.isLegacyBill)
            .map((invoice) => invoice.id);
        const billsIds = invoices
            .filter((invoice) => invoice.isLegacyBill)
            .map((invoice) => invoice.id);
        if (role === 'participant') {
            res = yield call(
                billingGateway.getProducerInvoicesZip,
                activeProducerId,
                invoicesIds,
                billsIds
            );
        } else {
            res = yield call(
                billingGateway.getInvoicesZip,
                operation.id,
                invoicesIds,
                billsIds
            );
        }
        const blob = new Blob([res.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download =
            res.headers['content-disposition']?.match(/filename="(.+)"/)?.[1] ||
            'invoices.zip';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        yield errorHandler(error);
    }
}

function* downloadInvoice(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const { operationId, invoiceId, language, isLegacyBill } = action;
        const url = yield call(
            billingGateway.getInvoiceDownloadURL,
            operationId,
            invoiceId,
            language,
            isLegacyBill
        );
        window.open(url);
    } catch (error) {
        yield errorHandler(error);
    }
}

function* cancelInvoice(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const { invoiceId } = action;
        const user = yield select(selectUser);
        const operation = yield select(selectSelectedOperation);
        if (user.role === 'participant') {
            const activeProducerId = yield select(selectActiveProducerId);
            yield call(
                billingGateway.cancelParticipantInvoice,
                activeProducerId,
                invoiceId
            );
        } else {
            yield call(billingGateway.cancelInvoice, operation.id, invoiceId);
        }
        let invoices = yield call(billingGateway.getInvoices, operation.id);
        yield put(setInvoices(invoices));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* changePaymentStatus(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const { invoiceId, paymentStatus } = action;
        const user = yield select(selectUser);
        const operation = yield select(selectSelectedOperation);
        if (user.role === 'participant') {
            const activeProducerId = yield select(selectActiveProducerId);
            yield call(
                billingGateway.changeParticipantInvoicePaymentStatus,
                activeProducerId,
                invoiceId,
                paymentStatus
            );
        } else {
            yield call(
                billingGateway.changeInvoicePaymentStatus,
                operation.id,
                invoiceId,
                paymentStatus
            );
        }
        let invoices = yield call(billingGateway.getInvoices, operation.id);
        yield put(setInvoices(invoices));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* publishInvoices(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const { operationId, invoiceIds } = action;
        const activeProducerId = yield select(selectActiveProducerId);
        if (activeProducerId) {
            yield call(
                billingGateway.publishProducerInvoices,
                activeProducerId,
                invoiceIds
            );
        } else {
            yield call(billingGateway.publishInvoices, operationId, invoiceIds);
        }

        let invoices = yield call(billingGateway.getInvoices, operationId);
        yield put(setInvoices(invoices));
        yield put(unsetInvoiceNotif(invoiceIds));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* getBillingPlans() {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        let billingPlans = yield call(
            billingGateway.getBillingPlans,
            operation.id
        );
        yield put(setBillingPlans(billingPlans));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* createBillingPlan(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        yield call(
            billingGateway.createBillingPlan,
            operation.id,
            action.entity
        );
        let billingPlans = yield call(
            billingGateway.getBillingPlans,
            operation.id
        );
        yield put(setBillingPlans(billingPlans));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* updateBillingPlan(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        yield call(
            billingGateway.updateBillingPlan,
            operation.id,
            action.entity
        );
        let billingPlans = yield call(
            billingGateway.getBillingPlans,
            operation.id
        );
        yield put(setBillingPlans(billingPlans));
    } catch (error) {
        if (error?.response?.data?.message === 'planAlreadyInvoiced') {
            yield put(
                setError({
                    message: i18n.t(
                        'enoapp.administrative.billing.plans.planAlreadyInvoiced'
                    ),
                    persist: true,
                })
            );
        } else {
            yield errorHandler(error);
        }
    }
}

function* getBillingSubscriptions() {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        const user = yield select(selectUser);
        let billingSubscriptions;
        if (user.role === 'participant') {
            const producerId = yield select(selectActiveProducerId);
            billingSubscriptions = yield call(
                billingGateway.getParticipantBillingSubscriptions,
                producerId
            );
        } else {
            billingSubscriptions = yield call(
                billingGateway.getBillingSubscriptions,
                operation.id
            );
        }

        yield put(setBillingSubscriptions(billingSubscriptions));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* createMultipleSubscriptions(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        const user = yield select(selectUser);
        let results;
        let billingSubscriptions;
        if (user.role === 'participant') {
            const activeProducerId = yield select(selectActiveProducerId);
            results = yield call(
                billingGateway.createProducerMultipleSubscriptions,
                activeProducerId,
                action.entity
            );
            billingSubscriptions = yield call(
                billingGateway.getParticipantBillingSubscriptions,
                activeProducerId
            );
        } else {
            results = yield call(
                billingGateway.createMultipleSubscriptions,
                operation.id,
                action.entity
            );
            billingSubscriptions = yield call(
                billingGateway.getBillingSubscriptions,
                operation.id
            );
        }

        if (results.errors.length > 0) {
            const consumers = yield select(selectConsumers);
            const producers = yield select(selectProducers);
            let errorMessages = [];
            for (let error of results.errors) {
                const producer = producers.find(
                    (producer) => producer.id === error.producerId
                );
                const consumer = consumers.find(
                    (consumer) => consumer.id === error.consumerId
                );
                if (!producer) {
                    errorMessages.push(
                        i18n.t('error.subscriptions.error_for_consumer', {
                            consumer: consumer?.name,
                        }) + i18n.t('error.subscriptions.' + error.message)
                    );
                } else {
                    errorMessages.push(
                        i18n.t('error.subscriptions.error_for_couple', {
                            producer: producer.name,
                            consumer: consumer?.name,
                        }) + i18n.t('error.subscriptions.' + error.message)
                    );
                }
            }
            yield put(
                setError({
                    message: errorMessages.join('\n'),
                    persist: true,
                })
            );
        }
        yield put(setBillingSubscriptions(billingSubscriptions));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* createBillingSubscription(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        yield call(
            billingGateway.createBillingSubscription,
            operation.id,
            action.entity
        );
        let billingSubscriptions = yield call(
            billingGateway.getBillingSubscriptions,
            operation.id
        );
        yield put(setBillingSubscriptions(billingSubscriptions));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* deleteBillingSubscription(action) {
    try {
        const billingGateway = yield getContext('billingNewGateway');
        const operation = yield select(selectSelectedOperation);
        const user = yield select(selectUser);
        let billingSubscriptions;

        if (user.role === 'participant') {
            const activeProducerId = yield select(selectActiveProducerId);
            yield call(
                billingGateway.deleteProducerBillingSubscription,
                activeProducerId,
                action.id
            );
            billingSubscriptions = yield call(
                billingGateway.getParticipantBillingSubscriptions,
                activeProducerId
            );
        } else {
            yield call(
                billingGateway.deleteBillingSubscription,
                operation.id,
                action.id
            );
            billingSubscriptions = yield call(
                billingGateway.getBillingSubscriptions,
                operation.id
            );
        }
        yield put(setBillingSubscriptions(billingSubscriptions));
    } catch (error) {
        yield errorHandler(error);
    }
}

function* getBillingEntityLogo(action) {
    try {
        const { billingEntity, operation } = action;
        const billingGateway = yield getContext('billingNewGateway');
        const logo = yield call(
            billingGateway.getBillingEntityLogo,
            billingEntity,
            operation
        );
        yield put(setBillingEntityLogo(logo));
    } catch (error) {
        yield put(
            setError({ status: error.response && error.response.status })
        );
        console.error(error);
    }
}

function* getLastBillRun() {
    try {
        const operationId = yield select(selectSelectedOperationId);
        const activeProducerId = yield select(selectActiveProducerId);
        const billingGateway = yield getContext('billingNewGateway');
        let billRun;
        if (activeProducerId) {
            billRun = yield call(
                billingGateway.getParticipantLastInvoicesRun,
                activeProducerId
            );
        } else {
            billRun = yield call(
                billingGateway.getLastInvoicesRun,
                operationId
            );
        }

        if (billRun.status !== 'PENDING') {
            yield put(setBillRun(billRun || {}));
        } else {
            setInvoiceGenerationProgress({ ...billRun.progress });
            yield call(getGenerationProgress, {
                billRun,
            });
        }
    } catch (error) {
        yield put(
            setError({ status: error.response && error.response.status })
        );
        console.error(error);
    }
}

export function* getBillableEntitiesSaga() {
    yield takeLatest(GET_BILLABLE_ENTITIES, getBillableEntities);
}

export function* getBillingEntitiesSaga() {
    yield takeLatest(GET_BILLING_ENTITIES, getBillingEntities);
}

export function* createBillingEntitySaga() {
    yield takeLatest(CREATE_BILLING_ENTITY, createBillingEntity);
}

export function* updateBillingEntitySaga() {
    yield takeLatest(UPDATE_BILLING_ENTITY, updateBillingEntity);
}

export function* createBillableEntitySaga() {
    yield takeLatest(CREATE_BILLABLE_ENTITY, createBillableEntity);
}

export function* updateBillableEntitySaga() {
    yield takeLatest(UPDATE_BILLABLE_ENTITY, updateBillableEntity);
}

export function* getInvoicesSaga() {
    yield takeLatest(GET_INVOICES, getInvoices);
}

export function* getLastInvoiceDateSaga() {
    yield takeLatest(GET_LAST_INVOICE_DATE, getLastInvoiceDate);
}

export function* generateInvoicesAsyncSaga() {
    yield takeLatest(GENERATE_INVOICES_ASYNC, generateInvoicesAsync);
}

export function* generateInvoicesSyncSaga() {
    yield takeLatest(GENERATE_INVOICES_SYNC, generateInvoicesSync);
}

export function* deleteInvoicesSaga() {
    yield takeLatest(DELETE_INVOICES, deleteInvoices);
}

export function* downloadAccountingSummarySaga() {
    yield takeLatest(DOWNLOAD_ACCOUNTING_SUMMARY, downloadAccountingSummary);
}

export function* downloadZipInvoicesSaga() {
    yield takeLatest(DOWNLOAD_ZIP_INVOICES, downloadZipInvoices);
}

export function* downloadInvoiceSaga() {
    yield takeLatest(DOWNLOAD_INVOICE, downloadInvoice);
}

export function* cancelInvoiceSaga() {
    yield takeLatest(CANCEL_INVOICE, cancelInvoice);
}

export function* changePaymentStatusSaga() {
    yield takeLatest(CHANGE_PAYMENT_STATUS, changePaymentStatus);
}

export function* publishInvoicesSaga() {
    yield takeLatest(PUBLISH_INVOICES, publishInvoices);
}

export function* getBillingPlansSaga() {
    yield takeLatest(GET_BILLING_PLANS, getBillingPlans);
}

export function* createBillingPlanSaga() {
    yield takeLatest(CREATE_BILLING_PLAN, createBillingPlan);
}

export function* updateBillingPlanSaga() {
    yield takeLatest(UPDATE_BILLING_PLAN, updateBillingPlan);
}

export function* getBillingSubscriptionsSaga() {
    yield takeLatest(GET_BILLING_SUBSCRIPTIONS, getBillingSubscriptions);
}

export function* createMultipleSubscriptionsSaga() {
    yield takeLatest(
        CREATE_MULTIPLE_SUBSCRIPTIONS,
        createMultipleSubscriptions
    );
}

export function* createBillingSubscriptionSaga() {
    yield takeLatest(CREATE_BILLING_SUBSCRIPTION, createBillingSubscription);
}

export function* deleteBillingSubscriptionSaga() {
    yield takeLatest(DELETE_BILLING_SUBSCRIPTION, deleteBillingSubscription);
}

export function* getBillingEntityLogoSaga() {
    yield takeLatest(GET_BILLING_ENTITY_LOGO, getBillingEntityLogo);
}

export function* getLastInvoicesRunSaga() {
    yield takeLatest(GET_LAST_BILL_RUN, getLastBillRun);
}

const billingSagas = [
    getBillableEntitiesSaga,
    createBillingEntitySaga,
    getBillingEntitiesSaga,
    updateBillingEntitySaga,
    getInvoicesSaga,
    cancelInvoiceSaga,
    changePaymentStatusSaga,
    getLastInvoiceDateSaga,
    generateInvoicesSyncSaga,
    generateInvoicesAsyncSaga,
    deleteInvoicesSaga,
    downloadAccountingSummarySaga,
    downloadZipInvoicesSaga,
    downloadInvoiceSaga,
    publishInvoicesSaga,
    getBillingPlansSaga,
    createBillingPlanSaga,
    updateBillingPlanSaga,
    getBillingSubscriptionsSaga,
    createMultipleSubscriptionsSaga,
    createBillingSubscriptionSaga,
    createBillableEntitySaga,
    updateBillableEntitySaga,
    getBillingEntityLogoSaga,
    deleteBillingSubscriptionSaga,
    getLastInvoicesRunSaga,
];

export default billingSagas;
